import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react'
import {
  Box,
  Typography,
  Paper,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material'
import { ProcessingSession, ProgressInfo, ExtractedQuestion } from '../../types'
import { config } from '../../config/environment'

export interface TabPlanDocumentUploadRef {
  triggerUploadAndExtraction: () => void
}

interface TabPlanDocumentUploadStepProps {
  onUploadComplete?: (session: ProcessingSession) => void
  onExtractionComplete?: (questions: ExtractedQuestion[]) => void
  onError?: (error: string) => void
  onProgressUpdate?: (progress: ProgressInfo) => void
  onFileSelected?: (hasFile: boolean) => void
  isLoading?: boolean
  autoExtract?: boolean
  modelId?: string
}

export const TabPlanDocumentUploadStep = forwardRef<TabPlanDocumentUploadRef, TabPlanDocumentUploadStepProps>(({
  onUploadComplete,
  onExtractionComplete,
  onError,
  onProgressUpdate,
  onFileSelected,
  isLoading = false,
  autoExtract = false,
  modelId
}, ref) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedDictionaryFile, setSelectedDictionaryFile] = useState<File | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isDictionaryDragOver, setIsDictionaryDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [showDictionaryDialog, setShowDictionaryDialog] = useState(false)
  const [dictionaryValidationMessage, setDictionaryValidationMessage] = useState('')
  const [pendingSession, setPendingSession] = useState<ProcessingSession | null>(null)

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ]
    
    if (!validTypes.includes(file.type)) {
      onError?.('Please upload a Word document (.docx or .doc)')
      return
    }

    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024
    if (file.size > maxSize) {
      onError?.('File size must be less than 100MB')
      return
    }

    setSelectedFile(file)
    onFileSelected?.(true)
  }, [onError, onFileSelected])

  const handleDictionaryFileSelect = useCallback((file: File) => {
    // Validate file type for CSV/TSV using MIME type (primary) and extension (fallback)
    const validMimeTypes = [
      'text/csv',
      'text/tab-separated-values',
      'text/tsv',
      'application/csv',
      'text/plain',
      'application/vnd.ms-excel' // Sometimes CSV files get this MIME type
    ]
    
    const fileExtension = file.name.toLowerCase().split('.').pop()
    const validExtensions = ['csv', 'tsv']
    
    const isValidMimeType = validMimeTypes.includes(file.type)
    const isValidExtension = validExtensions.includes(fileExtension || '')
    
    if (!isValidMimeType && !isValidExtension) {
      onError?.('Please upload a CSV or TSV file for the dictionary')
      return
    }

    // Validate file size (10MB limit for dictionary)
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      onError?.('Dictionary file size must be less than 10MB')
      return
    }

    setSelectedDictionaryFile(file)
  }, [onError])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
  }, [handleFileSelect])

  const handleDictionaryInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleDictionaryFileSelect(files[0])
    }
  }, [handleDictionaryFileSelect])

  const validateDictionary = async (sessionId: string): Promise<{ hasProblems: boolean; message: string }> => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/tabplan/validate-dictionary/${sessionId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Dictionary validation failed')
      }
      
      const result = await response.json()
      return {
        hasProblems: result.hasProblems || false,
        message: result.message || 'Dictionary validation completed'
      }
    } catch (error) {
      console.error('Dictionary validation error:', error)
      return {
        hasProblems: true,
        message: error instanceof Error ? error.message : 'Dictionary validation failed'
      }
    }
  }

  const extractQuestions = async (session: ProcessingSession) => {
    try {
      // Start question extraction - SSE will handle progress updates
      const extractResponse = await fetch(`${config.API_BASE_URL}/api/tabplan/extract`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: session.sessionId,
          modelId: modelId
        })
      })

      if (!extractResponse.ok) {
        const errorData = await extractResponse.json()
        throw new Error(errorData.message || 'Question extraction failed')
      }

      const extractResult = await extractResponse.json()
      
      if (!extractResult.success) {
        throw new Error(extractResult.message || 'Question extraction failed')
      }

      // SSE will handle all progress updates and completion via TabPlanWizard
      
    } catch (error) {
      console.error('Question extraction error:', error)
      onError?.(error instanceof Error ? error.message : 'Question extraction failed')
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    setUploadProgress(0)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)
      
      if (selectedDictionaryFile) {
        formData.append('dictionaryFile', selectedDictionaryFile)
      }

      // Simulate progress for better UX with extended duration for visibility
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = Math.min(prev + 5, 90)
          onProgressUpdate?.({
            type: 'processing',
            percentage: newProgress,
            message: newProgress < 30 
              ? 'Uploading document...' 
              : newProgress < 60 
                ? 'Processing document...' 
                : newProgress < 85
                  ? 'Preparing for AI analysis...'
                  : 'Finalizing upload...'
          })
          return newProgress
        })
      }, 500)

      // Upload to backend
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 60000) // 60 second timeout
      
      const response = await fetch(`${config.API_BASE_URL}/api/tabplan/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)

      clearInterval(progressInterval)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Upload failed')
      }

      setUploadProgress(100)
      onProgressUpdate?.({
        type: 'completion',
        percentage: 100,
        message: selectedDictionaryFile 
          ? 'Document and dictionary uploaded successfully!'
          : 'Document uploaded successfully!'
      })

      const result = await response.json()
      
      if (!result.success) {
        throw new Error(result.message || 'Upload failed')
      }

      // Create session object
      const session: ProcessingSession = {
        sessionId: result.sessionId,
        userId: 'anonymous',
        status: 'ready_for_review',
        questions: [],
        progress: {
          phase: 'complete',
          current: 1,
          total: 1,
          message: 'Document uploaded successfully'
        },
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000)
      }

      // If dictionary file was uploaded, validate it before proceeding
      if (selectedDictionaryFile) {
        const validation = await validateDictionary(session.sessionId)
        
        if (validation.hasProblems) {
          // Show confirmation dialog with problems found
          setDictionaryValidationMessage(validation.message)
          setPendingSession(session)
          setShowDictionaryDialog(true)
          return // Wait for user decision
        }
      }

      // Proceed with upload completion
      onUploadComplete?.(session)

      // If autoExtract is enabled, continue with question extraction
      if (autoExtract) {
        await extractQuestions(session)
      }

    } catch (error) {
      console.error('Upload error:', error)
      if (error instanceof Error && error.name === 'AbortError') {
        onError?.('Upload timed out after 60 seconds. Please try again.')
      } else {
        onError?.(error instanceof Error ? error.message : 'Upload failed')
      }
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  // Expose method to parent component (after handleUpload is defined)
  useImperativeHandle(ref, () => ({
    triggerUploadAndExtraction: () => {
      if (selectedFile) {
        handleUpload()
      }
    }
  }), [selectedFile, handleUpload])

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Upload Files
      </Typography>
      
      <Typography color="text.secondary" paragraph>
        Upload your survey questionnaire and optionally a dictionary file (recommended) for enhanced question extraction and correct choice codes.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        {/* Questionnaire File Upload */}
        <Box flex={1}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Survey Questionnaire (Required)
          </Typography>
          
          <Paper
            elevation={isDragOver ? 4 : 1}
            sx={{
              p: 3,
              border: isDragOver ? '2px dashed #008FA8' : '2px dashed #e0e0e0',
              backgroundColor: isDragOver ? 'rgba(0,143,168,0.06)' : 'transparent',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".docx,.doc"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
              disabled={isUploading || isLoading}
            />

            {selectedFile ? (
              <Stack spacing={1} alignItems="center">
                <DocumentIcon sx={{ fontSize: 40, color: 'success.main' }} />
                <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                  {selectedFile.name}
                </Typography>
                <Chip 
                  label={formatFileSize(selectedFile.size)} 
                  size="small" 
                  color="primary" 
                />
                <Chip 
                  icon={<CheckIcon />}
                  label="Valid Word Document" 
                  size="small" 
                  color="success" 
                />
              </Stack>
            ) : (
              <Stack spacing={1} alignItems="center">
                <UploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                <Typography variant="subtitle2" textAlign="center">
                  Click anywhere to browse and upload or drag & drop
                </Typography>
                <Typography color="text.secondary" variant="caption" textAlign="center">
                  .docx/.doc files up to 100MB
                </Typography>
              </Stack>
            )}
          </Paper>
        </Box>

        {/* Dictionary File Upload */}
        <Box flex={1}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Dictionary File (Recommended/Optional)
          </Typography>
          
          <Paper
            elevation={isDictionaryDragOver ? 4 : 1}
            sx={{
              p: 3,
              border: isDictionaryDragOver ? '2px dashed #008FA8' : '2px dashed #e0e0e0',
              backgroundColor: isDictionaryDragOver ? 'rgba(0,143,168,0.06)' : 'transparent',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onDrop={(e) => {
              e.preventDefault()
              setIsDictionaryDragOver(false)
              const files = e.dataTransfer.files
              if (files && files.length > 0) {
                handleDictionaryFileSelect(files[0])
              }
            }}
            onDragOver={(e) => {
              e.preventDefault()
              setIsDictionaryDragOver(true)
            }}
            onDragLeave={() => setIsDictionaryDragOver(false)}
            onClick={() => document.getElementById('dictionary-input')?.click()}
          >
            <input
              id="dictionary-input"
              type="file"
              accept=".csv,.tsv"
              style={{ display: 'none' }}
              onChange={handleDictionaryInputChange}
              disabled={isUploading || isLoading}
            />

            {selectedDictionaryFile ? (
              <Stack spacing={1} alignItems="center">
                <DocumentIcon sx={{ fontSize: 40, color: 'info.main' }} />
                <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                  {selectedDictionaryFile.name}
                </Typography>
                <Chip 
                  label={formatFileSize(selectedDictionaryFile.size)} 
                  size="small" 
                  color="info" 
                />
                <Chip 
                  icon={<CheckIcon />}
                  label="Valid Dictionary File" 
                  size="small" 
                  color="success" 
                />
              </Stack>
            ) : (
              <Stack spacing={1} alignItems="center">
                <UploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                <Typography variant="subtitle2" textAlign="center">
                  Click anywhere to browse and upload or drag & drop
                </Typography>
                <Typography color="text.secondary" variant="caption" textAlign="center">
                  CSV/TSV files up to 10MB
                </Typography>
              </Stack>
            )}
          </Paper>
        </Box>
      </Stack>

      {/* Help Text */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Tip:</strong> For best results, ensure your survey questions include:
          <br />• Clear question numbers (Q1, Q2, etc.) and question text
          <br />• Answer options/scales properly listed
          <br />• Question types specified (Single choice, Multiple choice, Scale, etc.)
          <br />• Base sizes mentioned (All respondents, Males only, etc.)
          <br />• Any special instructions or programming logic notes
        </Typography>
      </Alert>

      {/* Dictionary Validation Confirmation Dialog */}
      <Dialog
        open={showDictionaryDialog}
        onClose={() => setShowDictionaryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Dictionary Format Issues Found</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {dictionaryValidationMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowDictionaryDialog(false)
              setPendingSession(null)
              setDictionaryValidationMessage('')
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              setShowDictionaryDialog(false)
              
              if (pendingSession) {
                // Proceed without dictionary enhancement
                onUploadComplete?.(pendingSession)
                
                // If autoExtract is enabled, continue with question extraction
                if (autoExtract) {
                  await extractQuestions(pendingSession)
                }
              }
              
              setPendingSession(null)
              setDictionaryValidationMessage('')
            }}
            variant="contained"
            color="primary"
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
})

export default TabPlanDocumentUploadStep